import { useCustomFieldsStore } from "@/store/customField";
import { useResourcesStore } from "@/store/resource";
import { createResource, deleteResource, findResources, getResources, updateResource } from "../api/resource";
import { CustomField } from "../types/customField";
import { Resource, ResourceDTO, Timezone } from "../types/resource";
import { ResourceType } from "../types/resourceType";

export async function fetchResourcePagedData(params?: PagedRequestParams): Promise<PagedResult<Resource>> {
    const res = await getResources(params)

    const resourcesStore = useResourcesStore()
    const customFieldsStore = useCustomFieldsStore()
    // A no-op once /timezones has answered. It is here because dtoToResource below snapshots the
    // list, so mapping while the list is empty pins every resource to a null timezone: this makes
    // each resource load a retry point for a fetch that failed at login, which initializeAppStores'
    // memoised promise would otherwise never attempt again for the life of the tab.
    await Promise.all([customFieldsStore.fetchAll(), resourcesStore.ensureTimezonesLoaded()])

    const resources = res.items.map(dto => dtoToResource(dto, resourcesStore.types, resourcesStore.timezones, customFieldsStore.customFields));

    return {
        ...res,
        items: resources
    }
}

export async function manageCreateResource(data: Resource): Promise<ErrorMessage | Resource> {
    const res = await createResource(resourceToDTO(data))

    if (res && 'errorCode' in res) {
        return res
    }

    const resourcesStore = useResourcesStore()
    const customFieldsStore = useCustomFieldsStore()
    await customFieldsStore.fetchAll()

    const resource = dtoToResource(res, resourcesStore.types, resourcesStore.timezones, customFieldsStore.customFields);

    return resource;
}

export async function manageUpdateResource(data: Resource): Promise<ErrorMessage | Resource> {
    const res = await updateResource(resourceToDTO(data))

    if (res && 'errorCode' in res) {
        return res
    }

    const resourcesStore = useResourcesStore()
    const customFieldsStore = useCustomFieldsStore()

    const resource = dtoToResource(res, resourcesStore.types, resourcesStore.timezones, customFieldsStore.customFields);

    return resource;
}

export async function manageDeleteResource(id: string): Promise<ErrorMessage | void> {
    return await deleteResource(id)
}

export async function getResourcesByIds(ids: string[]): Promise<PagedResult<ResourceDTO>> {
    return findResources({ ids: ids })
}

export async function getResourcesByTypeIds(typeIds: string[]): Promise<PagedResult<ResourceDTO>> {
    return findResources({ resource_type_ids: typeIds })
}

function dtoToResource(dto: ResourceDTO, types: ResourceType[], timezones: Timezone[], resourceCustomFields: CustomField[]): Resource {
    const type = types.find(t => t.id === dto.resourceTypeId)!
    // D-09 / SC7: no non-null assertion. A miss means the stored id is not in the served list, which
    // must degrade visibly rather than hand a broken object downstream.
    const timezone = timezones.find(tz => tz.id === dto.timezoneId) ?? null
    const customFields = resourceCustomFields.filter(cf => dto.customFields.includes(cf.id))

    return {
        id: dto.id,
        type: type,
        name: dto.name,
        description: dto.description,
        location: dto.location,
        timezone: timezone,
        timezoneId: dto.timezoneId,
        capacity: dto.capacity,
        color: dto.color,
        customFields: customFields,
        enabled: dto.enabled
    }
}

function resourceToDTO(resource: Resource): ResourceDTO {
    return {
        id: resource.id,
        resourceTypeId: resource.type.id,
        name: resource.name,
        description: resource.description,
        location: resource.location,
        // The edit modal replaces `timezone` wholesale when the operator picks one, so the resolved
        // object wins when present; the raw stored id is the floor so saving a degraded resource
        // preserves its column instead of blanking it (D-09).
        timezoneId: resource.timezone?.id ?? resource.timezoneId,
        capacity: resource.capacity,
        color: resource.color,
        customFields: resource.customFields.map(cf => cf.id),
        enabled: resource.enabled
    }
}